/**
 * ReferenceMultiPicker — inline multi-select из справочника (по id).
 *
 * Generic-обёртка над InlinePicker. Display — Badge'и через gap.
 * Popover — список с галочками, клик тогглит выбор и НЕ закрывает popover.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  type MantineSize,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { InlinePicker } from './InlinePicker';
import type { ReferenceOption } from './ReferenceSinglePicker';

export interface ReferenceMultiPickerProps {
  /** Текущий набор выбранных id. */
  value: number[];
  /** Колбек с новым набором. */
  onChange: (ids: number[]) => void;
  /**
   * Лейблы выбранных значений для display.
   * Передавать из родителя (через JOIN), чтобы не делать запрос ради display.
   */
  selectedLabels?: ReadonlyArray<{ id: number; label: string }>;
  /** Одноразовая загрузка справочника при первом открытии popover. */
  fetchOptions: () => Promise<ReferenceOption[]>;
  /** Плейсхолдер если ничего не выбрано. */
  placeholder?: string;
  /** Цвет Badge'ей. По умолчанию 'gray'. */
  badgeColor?: string;
  /** Если false — display только. */
  canEdit?: boolean;
  /** Размер display badge'ей. */
  size?: MantineSize;
  /** Размер popover content. */
  inputSize?: MantineSize;
  /** Ширина popover. */
  width?: number;
}

type RowProps = {
  label: string;
  badgeColor: string;
  selected: boolean;
  onClick: () => void;
  size: MantineSize;
};

function OptionRow({ label, badgeColor, selected, onClick, size }: RowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: 4,
        background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <Group gap={8} wrap='nowrap' justify='space-between'>
        <Badge size={size} variant='light' color={badgeColor} style={{ textTransform: 'none' }}>
          {label}
        </Badge>
        {selected ? <IconCheck size={14} color='var(--mantine-color-blue-6)' /> : null}
      </Group>
    </Box>
  );
}

export function ReferenceMultiPicker({
  value,
  onChange,
  selectedLabels = [],
  fetchOptions,
  placeholder = 'Не указаны',
  badgeColor = 'gray',
  canEdit = true,
  size = 'sm',
  inputSize = 'sm',
  width = 300,
}: ReferenceMultiPickerProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<ReferenceOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened || options.length > 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await fetchOptions();
        if (!cancelled) setOptions(rows ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, options.length, fetchOptions]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id: number) => {
    const next = selectedSet.has(id) ? value.filter(v => v !== id) : [...value, id];
    onChange(next);
  };

  const display =
    selectedLabels.length > 0 ? (
      <Group gap={4} wrap='wrap'>
        {selectedLabels.map(item => (
          <Badge
            key={item.id}
            size='sm'
            variant='light'
            color={badgeColor}
            style={{ textTransform: 'none', cursor: 'inherit' }}
          >
            {item.label}
          </Badge>
        ))}
      </Group>
    ) : (
      <Text
        size={size}
        c='dimmed'
        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {placeholder}
      </Text>
    );

  return (
    <InlinePicker
      display={display}
      opened={opened}
      onOpenChange={setOpened}
      canEdit={canEdit}
      width={width}
    >
      <Stack gap={6}>
        <TextInput
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder='Поиск...'
          size={inputSize}
          autoFocus
        />
        <ScrollArea.Autosize mah={300} type='hover' scrollbarSize={6} scrollbars='y'>
          <Stack gap={2}>
            {filtered.map(o => (
              <OptionRow
                key={o.id}
                label={o.label}
                badgeColor={badgeColor}
                selected={selectedSet.has(o.id)}
                onClick={() => toggle(o.id)}
                size={inputSize}
              />
            ))}
            {loading ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Загрузка...
              </Text>
            ) : null}
            {!loading && filtered.length === 0 ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                {search ? 'Ничего не найдено' : 'Нет доступных вариантов'}
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </InlinePicker>
  );
}
